import styled from 'threads5/styles/styled';

// Scrolls sideways on a narrow screen instead of squeezing the columns. It can
// be focused, so a keyboard user can scroll it with the arrow keys.
export const TableScroll = styled('div')(({ theme }) => {
  return {
    overflowX: 'auto',
    margin: '24px 0',
    '&:focus-visible': {
      outlineWidth: '2px',
      outlineColor: theme.palette['web3/component/button/outline'],
      outlineStyle: 'solid',
      outlineOffset: '3px',
    },
  };
});

// The same borders, padding and type as the tables in the /legal documents
// (components-v3/Legal/LegalContent.tsx), so a table here can be held up next
// to one there. The colors come from the web3/component/table tokens, which are
// the /legal colors in light mode and flip in dark mode.
export const StyledTable = styled('table')(({ theme }) => {
  // In light mode the text keeps the page's default colors, as in the /legal
  // tables. In dark mode they would be dark on dark, so the text takes the
  // table's own color instead.
  const inheritTextColor = theme.palette.mode === 'dark' && {
    color: 'inherit',
  };

  return {
    borderCollapse: 'collapse',
    width: '100%',
    color: theme.palette['web3/component/table/text'],
    fontFamily: theme.typography.sans,
    fontSize: '2rem',
    lineHeight: 1.5,
    // The page's default table styles paint the sections and rows white. On a
    // dark background that shows as white boxes, so leave them clear and let
    // the container show through. The doubled & beats those defaults, and the
    // highlighted row below sets its background again.
    '&& thead, && tbody, && tfoot, && tr, && th, && td': {
      background: 'transparent',
      ...inheritTextColor,
    },
    '& caption': {
      captionSide: 'top',
      padding: '0 0 12px',
      textAlign: 'start',
      fontFamily: theme.typography.plaidSans,
      fontSize: '2rem',
      fontWeight: 600,
    },
    '& th, & td': {
      border: `1px solid ${theme.palette['web3/component/table/border']}`,
      padding: 20,
      verticalAlign: 'top',
      textAlign: 'start',
    },
    '& th': {
      padding: 10,
      fontFamily: theme.typography.plaidSans,
      fontSize: '2.4rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: 0,
    },
    '& p, & li': {
      ...inheritTextColor,
      fontSize: '2rem',
      lineHeight: 1.5,
    },
    '& p': {
      margin: '0 0 8px',
    },
    '& ul': {
      margin: '0 0 8px',
    },
    '& td > :last-child, & th > :last-child': {
      marginBottom: 0,
    },
    // A link takes the text color. The page's default link colors would win
    // over `inherit` without the doubled &, and are unreadable on dark.
    '&& a, && a:visited, && a:hover, && a:focus': {
      color: 'inherit',
      fontWeight: 600,
      textDecoration: 'underline',
    },
    '& .cms-table-row--highlight th, & .cms-table-row--highlight td': {
      background: theme.palette['web3/component/table/highlight-background'],
    },
  };
});
